import { Link } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SeoTags } from "../../components/Head/SeoTags";

const ExtensionPrivacy = () => {
  const { i18n } = useTranslation();
  const lastUpdated = new Date("2025-07-02").toLocaleDateString(i18n.language, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const [openAccordion, setOpenAccordion] = useState(null);

  const toggleAccordion = (accordionId) => {
    setOpenAccordion(openAccordion === accordionId ? null : accordionId);
  };

  return (
    <>
      <SeoTags
        title="Browser Extension Privacy Policy - onBookmarks"
        description="Learn exactly how the onBookmarks browser extension uses permissions, handles your data, and protects your privacy. Clear explanations for Chrome, Firefox, and Safari."
        keywords="browser extension privacy, chrome extension permissions, firefox addon privacy, safari extension security, browser permissions explained, onBookmarks extension"
        canonical="https://onbookmarks.com/legal/extension-privacy-policy/"
        localized={false}
      />

      {/* Enhanced WebPage Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Browser Extension Privacy Policy - onBookmarks",
            description:
              "Comprehensive privacy policy for the onBookmarks browser extension, explaining permissions, data handling, and security practices in simple terms.",
            url: "https://onbookmarks.com/legal/extension-privacy-policy",
            isPartOf: {
              "@type": "WebSite",
              name: "onBookmarks",
              url: "https://onbookmarks.com",
            },
            lastReviewed: lastUpdated,
            about: {
              "@type": "SoftwareApplication",
              name: "onBookmarks Browser Extension",
              applicationCategory: "BrowserApplication",
            },
          }),
        }}
      />

      <div className="min-vh-100">
        {/* Hero Section */}
        <div className="position-relative overflow-hidden bg-gradient-purple text-white py-5">
          <div className="container py-lg-5">
            <div className="row justify-content-center">
              <div className="col-lg-8 text-center">
                <div className="d-inline-flex align-items-center bg-white bg-opacity-25 rounded-pill px-3 py-2 mb-4">
                  <i className="bi bi-puzzle me-2"></i>
                  <span className="fw-medium">Updated {lastUpdated}</span>
                </div>

                <h1 className="display-4 fw-bold mb-4 text-white">
                  Browser Extension Privacy
                </h1>
                <p
                  className="lead opacity-75 mx-auto"
                  style={{ maxWidth: "42rem" }}
                >
                  We believe in transparency. Here's exactly what our browser
                  extension can access, why it needs each permission, and how we
                  protect your data.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Summary Cards */}
        <div
          className="container"
          style={{ marginTop: "-3rem", position: "relative", zIndex: 10 }}
        >
          <div className="row g-4 justify-content-center">
            <div className="col-md-6 col-lg-4">
              <div className="card border-0 shadow-lg h-100">
                <div className="card-body p-4">
                  <div
                    className="bg-primary bg-opacity-10 rounded-3 d-inline-flex align-items-center justify-content-center mb-3"
                    style={{ width: "48px", height: "48px" }}
                  >
                    <i className="bi bi-shield-lock-fill fs-4 text-primary"></i>
                  </div>
                  <h3 className="h5 fw-semibold mb-2">Minimal Permissions</h3>
                  <p className="text-muted mb-0">
                    We only request permissions absolutely necessary for
                    bookmarking
                  </p>
                </div>
              </div>
            </div>

            <div className="col-md-6 col-lg-4">
              <div className="card border-0 shadow-lg h-100">
                <div className="card-body p-4">
                  <div
                    className="bg-success bg-opacity-10 rounded-3 d-inline-flex align-items-center justify-content-center mb-3"
                    style={{ width: "48px", height: "48px" }}
                  >
                    <i className="bi bi-incognito fs-4 text-success"></i>
                  </div>
                  <h3 className="h5 fw-semibold mb-2">No Browsing Tracking</h3>
                  <p className="text-muted mb-0">
                    We don't track your browsing - only process pages you
                    bookmark
                  </p>
                </div>
              </div>
            </div>

            <div className="col-md-6 col-lg-4">
              <div className="card border-0 shadow-lg h-100">
                <div className="card-body p-4">
                  <div
                    className="bg-warning bg-opacity-10 rounded-3 d-inline-flex align-items-center justify-content-center mb-3"
                    style={{ width: "48px", height: "48px" }}
                  >
                    <i className="bi bi-hand-index-thumb fs-4 text-warning"></i>
                  </div>
                  <h3 className="h5 fw-semibold mb-2">You're in Control</h3>
                  <p className="text-muted mb-0">
                    Disable any permission or the entire extension anytime
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container py-5">
          {/* Introduction */}
          <section className="mb-5">
            <p className="lead text-muted">
              This policy explains how our browser extension works, what
              permissions it needs, and why. We've designed it to be as
              privacy-friendly as possible while still providing powerful
              bookmark management.
            </p>
            <p className="text-muted">
              <strong>The Short Version:</strong> We only access web pages when
              you actively bookmark them. We don't track your browsing, sell
              your data, or access information from other tabs or extensions.
            </p>
            <div
              className="alert alert-info d-flex align-items-start mt-4"
              role="alert"
            >
              <i className="bi bi-info-circle-fill me-2 mt-1"></i>
              <div>
                This policy supplements our main{" "}
                <Link to="/legal/privacy-policy" className="alert-link">
                  Privacy Policy
                </Link>
                . For general data handling, please refer to that document.
              </div>
            </div>
          </section>

          {/* Permissions Explained */}
          <section className="mb-5">
            <h2 className="h3 fw-bold mb-4 d-flex align-items-center">
              <i className="bi bi-key fs-3 text-primary me-3"></i>
              Browser Permissions Explained
            </h2>

            <p className="text-muted mb-4">
              Here's every permission our extension requests and exactly why we
              need it:
            </p>

            {/* Storage Permission */}
            <div className="mb-4">
              <div className="bg-primary bg-opacity-10 rounded-3 p-4">
                <h3 className="h5 fw-semibold mb-2">Storage Permission</h3>
                <p className="text-muted mb-3">
                  Saves your preferences and session locally on your device
                </p>

                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="bg-white bg-opacity-75 rounded-3 p-3">
                      <h4 className="h6 fw-medium mb-2">What it stores:</h4>
                      <ul className="list-unstyled text-muted mb-0">
                        <li>• Your login session</li>
                        <li>• Extension preferences</li>
                        <li>• Selected collections</li>
                        <li>• Temporary bookmark data</li>
                      </ul>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="bg-white bg-opacity-75 rounded-3 p-3">
                      <h4 className="h6 fw-medium mb-2">Privacy guarantee:</h4>
                      <p className="text-muted mb-0">
                        <i className="bi bi-check-circle-fill text-success me-1"></i>
                        Data stays on your device
                      </p>
                      <p className="text-muted mb-0">
                        <i className="bi bi-check-circle-fill text-success me-1"></i>
                        Other sites can't access it
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs Permission */}
            <div className="mb-4">
              <div className="bg-success bg-opacity-10 rounded-3 p-4">
                <h3 className="h5 fw-semibold mb-2">Tabs Permission</h3>
                <p className="text-muted mb-3">
                  Detects the current page to enable bookmarking
                </p>

                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="bg-white bg-opacity-75 rounded-3 p-3">
                      <h4 className="h6 fw-medium mb-2">Why we need it:</h4>
                      <ul className="list-unstyled text-muted mb-0">
                        <li>• Get current page URL & title</li>
                        <li>• Update extension icon</li>
                        <li>• Enable quick bookmarking</li>
                        <li>• Show bookmark status</li>
                      </ul>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="bg-white bg-opacity-75 rounded-3 p-3">
                      <h4 className="h6 fw-medium mb-2">What we DON'T do:</h4>
                      <ul className="list-unstyled text-danger mb-0">
                        <li>✗ Track your browsing history</li>
                        <li>✗ Log pages you don't bookmark</li>
                        <li>✗ Access other tab data</li>
                        <li>✗ Sell browsing data</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div
                  className="alert alert-success d-flex align-items-center mt-3 mb-0"
                  role="alert"
                >
                  <i className="bi bi-shield-check me-2"></i>
                  <small>
                    <strong>Privacy First:</strong> Tab data is only processed
                    when you actively choose to bookmark a page.
                  </small>
                </div>
              </div>
            </div>

            {/* ActiveTab Permission */}
            <div className="mb-4">
              <div className="bg-warning bg-opacity-10 rounded-3 p-4">
                <h3 className="h5 fw-semibold mb-2">ActiveTab Permission</h3>
                <p className="text-muted mb-3">
                  Only activates when you click our extension icon
                </p>

                <div className="bg-white bg-opacity-75 rounded-3 p-3">
                  <p className="text-muted mb-2">
                    This is the most privacy-friendly permission. It only grants
                    access to the current tab when you explicitly interact with
                    our extension.
                  </p>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-hand-index-thumb text-warning me-2"></i>
                    <span className="fw-medium">
                      You click = We access (that tab only)
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bookmarks Permission */}
            <div className="mb-4">
              <div className="bg-purple bg-opacity-10 rounded-3 p-4">
                <h3 className="h5 fw-semibold mb-2">Bookmarks Permission</h3>
                <p className="text-muted mb-3">
                  Syncs with your browser's built-in bookmarks (optional)
                </p>

                <div className="bg-white bg-opacity-75 rounded-3 p-3">
                  <h4 className="h6 fw-medium mb-2">Features enabled:</h4>
                  <ul className="list-unstyled text-muted mb-0">
                    <li>
                      <i className="bi bi-toggle-on text-purple me-2"></i>
                      Import existing browser bookmarks
                    </li>
                    <li>
                      <i className="bi bi-arrow-repeat text-purple me-2"></i>
                      Auto-sync new bookmarks (if enabled)
                    </li>
                    <li>
                      <i className="bi bi-folder-check text-purple me-2"></i>
                      Preserve folder organization
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Other Permissions */}
            <div className="accordion mb-4" id="otherPermissions">
              <div className="accordion-item border-0 mb-2">
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button bg-light ${
                      openAccordion === "scripting" ? "" : "collapsed"
                    }`}
                    type="button"
                    onClick={() => toggleAccordion("scripting")}
                    aria-expanded={openAccordion === "scripting"}
                  >
                    <i className="bi bi-code-slash me-2"></i>
                    Scripting Permission
                  </button>
                </h2>
                <div
                  id="scriptingPermission"
                  className={`accordion-collapse ${
                    openAccordion === "scripting" ? "collapse show" : "collapse"
                  }`}
                >
                  <div className="accordion-body">
                    <p className="text-muted">
                      Runs scripts to show notifications and enhance UI
                    </p>
                    <ul className="text-muted">
                      <li>Display save confirmations</li>
                      <li>Show floating bookmark button</li>
                      <li>Auto-login on onBookmarks sites</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 mb-2">
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button bg-light ${
                      openAccordion === "contextMenus" ? "" : "collapsed"
                    }`}
                    type="button"
                    onClick={() => toggleAccordion("contextMenus")}
                    aria-expanded={openAccordion === "contextMenus"}
                  >
                    <i className="bi bi-menu-button-wide me-2"></i>
                    Context Menus Permission
                  </button>
                </h2>
                <div
                  id="contextMenus"
                  className={`accordion-collapse ${
                    openAccordion === "contextMenus"
                      ? "collapse show"
                      : "collapse"
                  }`}
                >
                  <div className="accordion-body">
                    <p className="text-muted">
                      Adds "Save to onBookmarks" to right-click menu
                    </p>
                    <ul className="text-muted">
                      <li>Quick bookmark any link</li>
                      <li>Save images and selections</li>
                      <li>Works on any webpage</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0">
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button bg-light ${
                      openAccordion === "sidePanel" ? "" : "collapsed"
                    }`}
                    type="button"
                    onClick={() => toggleAccordion("sidePanel")}
                    aria-expanded={openAccordion === "sidePanel"}
                  >
                    <i className="bi bi-layout-sidebar me-2"></i>
                    Side Panel Permission (Chrome only)
                  </button>
                </h2>
                <div
                  id="sidePanel"
                  className={`accordion-collapse ${
                    openAccordion === "sidePanel" ? "collapse show" : "collapse"
                  }`}
                >
                  <div className="accordion-body">
                    <p className="text-muted">
                      Opens extension in Chrome's side panel
                    </p>
                    <ul className="text-muted">
                      <li>Better multitasking experience</li>
                      <li>Doesn't block your browsing</li>
                      <li>Persistent bookmark access</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Host Permissions */}
          <section className="mb-5">
            <h2 className="h3 fw-bold mb-4 d-flex align-items-center">
              <i className="bi bi-globe2 fs-3 text-primary me-3"></i>
              Website Access
            </h2>

            <p className="text-muted mb-4">
              Our extension only communicates with onBookmarks servers:
            </p>

            <div className="row g-3">
              <div className="col-md-4">
                <div className="card h-100">
                  <div className="card-body">
                    <h4 className="h6 fw-semibold d-flex align-items-center mb-2">
                      <i className="bi bi-cloud-arrow-up text-primary me-2"></i>
                      api.onbookmarks.com
                    </h4>
                    <p className="text-muted mb-0">
                      Saves and retrieves your bookmarks
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="card h-100">
                  <div className="card-body">
                    <h4 className="h6 fw-semibold d-flex align-items-center mb-2">
                      <i className="bi bi-person-check text-success me-2"></i>
                      my.onbookmarks.com
                    </h4>
                    <p className="text-muted mb-0">
                      Handles authentication & settings
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="card h-100 border-warning">
                  <div className="card-body">
                    <h4 className="h6 fw-semibold d-flex align-items-center mb-2">
                      <i className="bi bi-pc-display text-warning me-2"></i>
                      localhost:3000
                    </h4>
                    <p className="text-muted mb-0">Development testing only</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* What We Collect */}
          <section className="mb-5">
            <h2 className="h3 fw-bold mb-4 d-flex align-items-center">
              <i className="bi bi-database fs-3 text-success me-3"></i>
              Data Collection
            </h2>

            <div className="row g-4">
              <div className="col-md-6">
                <div className="bg-success bg-opacity-10 rounded-3 p-4 h-100">
                  <h3 className="h5 fw-semibold mb-3 text-success">
                    <i className="bi bi-check-circle me-2"></i>
                    What We Collect
                  </h3>
                  <ul className="list-unstyled text-muted mb-0">
                    <li className="mb-2">
                      <i className="bi bi-bookmark-plus text-success me-2"></i>
                      Pages you choose to bookmark
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-key text-success me-2"></i>
                      Secure authentication tokens
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-gear text-success me-2"></i>
                      Your extension preferences
                    </li>
                    <li>
                      <i className="bi bi-bug text-success me-2"></i>
                      Anonymous error reports
                    </li>
                  </ul>
                </div>
              </div>

              <div className="col-md-6">
                <div className="bg-danger bg-opacity-10 rounded-3 p-4 h-100">
                  <h3 className="h5 fw-semibold mb-3 text-danger">
                    <i className="bi bi-x-circle me-2"></i>
                    What We DON'T Collect
                  </h3>
                  <ul className="list-unstyled text-muted mb-0">
                    <li className="mb-2">
                      <i className="bi bi-clock-history text-danger me-2"></i>
                      Your browsing history
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-person-x text-danger me-2"></i>
                      Personal data from sites
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-key-fill text-danger me-2"></i>
                      Passwords or form data
                    </li>
                    <li>
                      <i className="bi bi-puzzle text-danger me-2"></i>
                      Data from other extensions
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Security */}
          <section className="mb-5">
            <h2 className="h3 fw-bold mb-4 d-flex align-items-center">
              <i className="bi bi-shield-check fs-3 text-primary me-3"></i>
              Security & Control
            </h2>

            <div className="bg-primary bg-opacity-10 rounded-3 p-4">
              <div className="row g-3">
                <div className="col-md-6">
                  <h4 className="h5 fw-semibold mb-3">Security Measures</h4>
                  <ul className="list-unstyled text-muted">
                    <li className="mb-2">
                      <i className="bi bi-lock-fill text-primary me-2"></i>
                      HTTPS encryption for all data
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-shield-lock text-primary me-2"></i>
                      Secure token storage
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-file-lock text-primary me-2"></i>
                      Client-side encryption available
                    </li>
                    <li>
                      <i className="bi bi-house-lock text-primary me-2"></i>
                      Isolated from other extensions
                    </li>
                  </ul>
                </div>

                <div className="col-md-6">
                  <h4 className="h5 fw-semibold mb-3">Your Control</h4>
                  <ul className="list-unstyled text-muted">
                    <li className="mb-2">
                      <i className="bi bi-toggles text-primary me-2"></i>
                      Disable specific permissions
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-x-square text-primary me-2"></i>
                      Remove extension anytime
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-arrow-repeat text-primary me-2"></i>
                      Control auto-sync settings
                    </li>
                    <li>
                      <i className="bi bi-incognito text-primary me-2"></i>
                      Use incognito mode
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Browser-Specific Notes */}
          <section className="mb-5">
            <h2 className="h3 fw-bold mb-4 d-flex align-items-center">
              <i className="bi bi-browser-chrome fs-3 text-primary me-3"></i>
              Browser-Specific Information
            </h2>

            <div className="row g-3">
              <div className="col-md-4">
                <div className="card h-100 border-primary">
                  <div className="card-body">
                    <h4 className="h6 fw-semibold d-flex align-items-center mb-3">
                      <i className="bi bi-browser-chrome text-primary me-2"></i>
                      Chrome
                    </h4>
                    <ul className="list-unstyled text-muted mb-0">
                      <li>• Manifest V3 compliant</li>
                      <li>• Side panel support</li>
                      <li>• Chrome Web Store verified</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="card h-100 border-warning">
                  <div className="card-body">
                    <h4 className="h6 fw-semibold d-flex align-items-center mb-3">
                      <i className="bi bi-browser-firefox text-warning me-2"></i>
                      Firefox
                    </h4>
                    <ul className="list-unstyled text-muted mb-0">
                      <li>• WebExtensions API</li>
                      <li>• Enhanced tracking protection</li>
                      <li>• Add-on policies compliant</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="card h-100 border-info">
                  <div className="card-body">
                    <h4 className="h6 fw-semibold d-flex align-items-center mb-3">
                      <i className="bi bi-browser-safari text-info me-2"></i>
                      Safari
                    </h4>
                    <ul className="list-unstyled text-muted mb-0">
                      <li>• Safari guidelines compliant</li>
                      <li>• Intelligent tracking prevention</li>
                      <li>• Browser.* namespace</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Quick Actions */}
        </div>
      </div>
    </>
  );
};

export default ExtensionPrivacy;
